import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import cn from 'classnames'
import {
	CircleIcon,
	CrownIcon,
	InfoOutlinedIcon,
	LikeIcon,
	StarIcon,
} from 'assets/icons'
import ListWrapper from '../ListWrapper'
import useRelations from 'hooks/api/useRelations'
import useUsers from 'hooks/api/useUsers'
import { getImageProcessorUrl, optionsParser, t } from 'helpers'
import { filterDuplicates } from 'helpers/propertiesParser'
import { EventBus } from 'helpers/EventBus'
import { useAppStore, useCastingStore, useUserStore } from 'store'
import {
	CASTING_STATUS,
	DROPDOWN_OPTIONS,
	PROJECT_ACCESS,
	RELATIONS,
} from 'types/enums'
import { User } from 'types/user'
import { PATHS } from 'types/enums'
import s from './index.module.scss'
import { addRelation } from 'api/relations'
import Tooltip from 'components/Tooltip'
import { useShallow } from 'zustand/react/shallow'
import useCastingOverviewFilter from 'hooks/useCastingOverviewFilter'
dayjs.extend(isBetween)

const tabs = [
	{
		label: (
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}>
				<span
					className="body2-s"
					style={{
						color: 'var(--mono300)',
						transition: 'var(--transition)',
					}}>
					{t('APP_NEW')}
				</span>
			</div>
		),
		value: 'new',
	},
	{
		label: (
			<LikeIcon
				width="16"
				height="16"
				color="var(--mono300)"
				style={{ transform: 'rotate(180deg)' }}
			/>
		),
		value: CASTING_STATUS.DISLIKED,
	},
	{
		label: <CircleIcon color="var(--mono300)" width="16" height="16" />,
		value: CASTING_STATUS.NEUTRAL,
	},
	{
		label: <LikeIcon color="var(--mono300)" width="16" height="16" />,
		value: CASTING_STATUS.LIKED,
	},
	{
		label: <StarIcon color="var(--mono300)" width="16" height="16" />,
		value: CASTING_STATUS.FAVORITE,
	},
]

const endDate = dayjs()
const startDate = endDate.subtract(7, 'day')
const isLastWeek = (createdDate: string): boolean =>
	dayjs(createdDate).isBetween(startDate, endDate, null, '[]')

const Candidates = (): JSX.Element => {
	const navigate = useNavigate()
	const { productionId, roleId, userId } = useParams()
	const [search, setSearch] = useState('')
	const { filter, setFilter } = useCastingOverviewFilter('candidates')
	const [collapsed, setCollapsed, access] = useCastingStore(
		useShallow((state) => [
			state.collapsed.candidates,
			state.setCollapsed,
			state.projectAccess,
		]),
	)

	const { data: relations, refresh } = useRelations(
		RELATIONS.ACCESS,
		roleId || '',
		'Content',
	)

	const data = relations?.sort(
		(a, b) =>
			new Date(b?.createdDate).valueOf() - new Date(a?.createdDate).valueOf(),
	)

	const winner = data?.find(
		(rel) => rel.status === CASTING_STATUS.SELECTED,
	)?.targetId

	const { users } = useUsers(
		data
			?.filter(
				(relation) =>
					relation.sourceId === roleId &&
					[
						CASTING_STATUS.APPLIED,
						CASTING_STATUS.FAVORITE,
						CASTING_STATUS.LIKED,
						CASTING_STATUS.NEUTRAL,
						CASTING_STATUS.DISLIKED,
						CASTING_STATUS.SELECTED,
						CASTING_STATUS.RESUBMITED,
					].includes(relation.status as CASTING_STATUS),
			)
			?.map((relation) => relation.targetId),
	)

	const genderOptions = optionsParser(DROPDOWN_OPTIONS.GENDERS)
	const professionOptions = optionsParser(DROPDOWN_OPTIONS.PROFESSION)

	const onUserClick = useCallback(
		(id: string): void => {
			// const { setOpenCastingRoles } = useCastingStore.getState()
			// setOpenCastingRoles(false)
			const { user } = useUserStore.getState()
			const hash = window.location.hash.split('-')
			navigate(
				`/${user?.CustomerRole?.toLowerCase()}/${
					PATHS.CASTING
				}/${productionId}/${roleId}/${id}` +
					hash?.[0] +
					'-0',
			)
		},
		[navigate, productionId, roleId],
	)

	const changeWinner = useCallback(
		async (userId: string, status: CASTING_STATUS): Promise<void> => {
			try {
				await addRelation({
					sourceId: roleId,
					targetId: userId,
					type: 'Content',
					relation: RELATIONS.ACCESS,
					status: status,
				})
			} catch (error) {
				console.log('error:', error)
			}
		},
		[roleId],
	)

	const handleWinnerMark = useCallback(
		async (userId: string): Promise<void> => {
			if (winner) {
				await changeWinner(winner, CASTING_STATUS.FAVORITE)
			}
			if (winner !== userId) await changeWinner(userId, CASTING_STATUS.SELECTED)
			await refresh()
		},
		[changeWinner, refresh, winner],
	)

	const list = useMemo(
		() =>
			filterDuplicates(users || [], 'ContentId')
				?.filter((user) =>
					filter
						? data?.find(
								(relation) =>
									(filter === 'new'
										? isLastWeek(relation.createdDate)
										: (filter === CASTING_STATUS.FAVORITE
												? [filter, CASTING_STATUS.SELECTED]
												: [filter]
										  ).includes(relation.status as string)) &&
									user?.ContentId === relation.targetId,
						  )
						: user,
				)
				?.filter((user) =>
					user?.ContentTitle?.toLowerCase().includes(search.toLowerCase()),
				) as unknown as User[],
		[search, users, data, filter],
	)

	useEffect(() => {
		EventBus.$on('refreshCastingCandidates', () => {
			refresh()
		})

		return () => {
			EventBus.$off('refreshCastingCandidates')
		}
	}, [refresh])

	useEffect(() => {
		EventBus.$on('nextCandidate', () => {
			const currentIndex = list.findIndex((u) => u.ContentId === userId)
			const nextIndex = list?.[currentIndex + 1] ? currentIndex + 1 : null
			if (nextIndex === null) return

			onUserClick(list[nextIndex].ContentId)
		})
		EventBus.$on('prevCandidate', () => {
			const currentIndex = list.findIndex((u) => u.ContentId === userId)
			const prevIndex = list?.[currentIndex - 1] ? currentIndex - 1 : null
			if (prevIndex === null) return

			onUserClick(list[prevIndex].ContentId)
		})

		return () => {
			EventBus.$off('nextCandidate')
			EventBus.$off('prevCandidate')
		}
	}, [list, onUserClick, userId])

	return (
		<ListWrapper
			type="users"
			users={list}
			tabs={tabs}
			filter={filter || ''}
			collapsed={collapsed}
			setCollapsed={(col) => setCollapsed({ candidates: col })}
			setFilter={setFilter}
			notifyFilters={
				access && access === PROJECT_ACCESS.READ
					? []
					: [
							CASTING_STATUS.LIKED,
							CASTING_STATUS.DISLIKED,
							CASTING_STATUS.FAVORITE,
					  ]
			}
			setSearch={setSearch}>
			<>
				{list?.length ? (
					list?.map((user, index) => (
						<div
							className={cn(s.user, {
								[s.active]: userId === user.ContentId,
								[s.winner]: winner === user.ContentId,
							})}
							key={user?.ContentId + index}>
							<Tooltip title={collapsed ? user?.ContentTitle : ''}>
								<div
									className={s.avatar}
									onClick={() => onUserClick(user.ContentId)}>
									<img
										src={
											getImageProcessorUrl(user?.CustomerImageSquareUrl, 48) ||
											'/placeholder.png'
										}
										alt={user?.ContentTitle}
									/>
								</div>
							</Tooltip>
							<div
								className={s.info}
								onClick={() => onUserClick(user.ContentId)}
								style={{
									transition: 'var(--transition)',
									visibility: collapsed ? 'hidden' : 'visible',
									opacity: collapsed ? '0' : '1',
								}}>
								<span className="body1-m textEllipsis">
									{user?.ContentTitle}
								</span>
								<span
									className="body2-r textEllipsis"
									style={{ color: 'var(--mono200)' }}>
									{[
										professionOptions?.find(
											(option) => option.value === user?.CustomerProfession,
										)?.label,
										genderOptions?.find(
											(option) => option.value === user?.CustomUserGender,
										)?.label,
										dayjs().diff(dayjs(user?.CustomUserBirthDate), 'year'),
									]
										.filter((v) => !!v)
										.join(', ')}
								</span>
							</div>
							<div className={s.iconsWrapper}>
								{[CASTING_STATUS.FAVORITE, CASTING_STATUS.SELECTED].includes(
									data?.find((rel) => rel.targetId === user?.ContentId)
										?.status as CASTING_STATUS,
								) ? (
									<div
										className={cn(s.winnerIcon, {
											[s.activeWinner]: winner === user?.ContentId,
											[s.collapsedWinner]:
												collapsed && winner === user?.ContentId,
										})}
										onClick={() => {
											if (
												collapsed ||
												(access && access !== PROJECT_ACCESS.FULLACCESS)
											)
												return

											handleWinnerMark(user?.ContentId)
										}}
										style={
											collapsed
												? {
														pointerEvents: 'none',
												  }
												: {}
										}>
										<CrownIcon
											color={
												winner === user?.ContentId
													? undefined
													: 'var(--mono400)'
											}
										/>
									</div>
								) : null}

								<div
									onClick={() => {
										const { setUserInfoSidebar } = useAppStore.getState()
										setUserInfoSidebar(user.ContentId)
									}}
									className={s.infoButton}>
									<InfoOutlinedIcon />
								</div>
							</div>
						</div>
					))
				) : (
					<span style={{ whiteSpace: 'nowrap' }}>
						{t('APP_RESULT_NOT_FOUND')}
					</span>
				)}
			</>
		</ListWrapper>
	)
}

export default Candidates
